import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { IActionCategory, IComponentType, IEasing, IPresetAnimation, IScormEventType, ISocialProvider } from '@r3f-component-data-structure';
import { FacebookIcon, InstagramIcon, LinkedinIcon, RedditIcon, SoundcloudIcon, SpotifyIcon, TiktokIcon, TwitchIcon, TwitterIcon, VimeoIcon, YoutubeIcon } from '@r3f/components/assets/icons';
import { isAbstractComponent } from '@r3f/utils/general';
import React from 'react';
import { WebsiteIconSvg } from '../assets/icons';
import AnimateModel16 from '../assets/icons/AnimateModel16';
import AnimateParticle16 from '../assets/icons/AnimateParticle16';
import Camera16 from '../assets/icons/Camera16';
import Danger16 from '../assets/icons/Danger16';
import Expand16 from '../assets/icons/Expand16';
import Facebook16 from '../assets/icons/Facebook16';
import Globe16 from '../assets/icons/Globe16';
import Hash16 from '../assets/icons/Hash16';
import Instagram16 from '../assets/icons/Instagram16';
import Learn16 from '../assets/icons/Learn16';
import LinkedIn16 from '../assets/icons/LinkedIn16';
import Mail16 from '../assets/icons/Mail16';
import Message16 from '../assets/icons/Message16';
import Phone16 from '../assets/icons/Phone16';
import Plus16 from '../assets/icons/Plus16';
import Reddit16 from '../assets/icons/Reddit16';
import Scene16 from '../assets/icons/Scene16';
import SearchIcon from '../assets/icons/search';
import Sound16 from '../assets/icons/Sound16';
import SoundCloud16 from '../assets/icons/SoundCloud16';
import Spotify16 from '../assets/icons/Spotify16';
import Star16 from '../assets/icons/Star16';
import TikTok16 from '../assets/icons/TikTok16';
import Twitch16 from '../assets/icons/Twitch16';
import Twitter16 from '../assets/icons/Twitter16';
import User16 from '../assets/icons/User16';
import View16 from '../assets/icons/View16';
import Vimeo16 from '../assets/icons/Vimeo16';
import YouTube16 from '../assets/icons/YouTube16';
import { addUrlScheme, capitalize, getUsernameFromSocialUrl, parseCustomUrl, parseEmailAddress, parsePhoneNumber } from '../utils/general';
import { ACTION_ERROR_MESSAGES } from './constants';
import { socialRegex as regex } from './social';
// Takes an action and returns a label for it, defaulting to noAction if no match
export const actionToLabel = (action) => {
    if (!action)
        return IActionCategory.noAction;
    return action.type;
};
const finaliseTitle = (title) => {
    return title ? capitalize(title) : ' - ';
};
// Takes an action, returns the title for designer
export const actionTitle = (action, componentsById) => {
    switch (action.type) {
        case IActionCategory.dialNumber: {
            return `Dial: ${action.tel || ' - '}`;
        }
        case IActionCategory.playSound: {
            return `Play sound: ${finaliseTitle(action.title)}`;
        }
        case IActionCategory.composeEmail:
            return `Compose email: ${action.recipient || ' - '}`;
        case IActionCategory.animateModel: {
            const entityTitle = finaliseTitle(componentsById[action.targetIds[0]]?.title);
            const animationName = finaliseTitle(action.animationName);
            return `${animationName}: ${entityTitle}`;
        }
        case IActionCategory.animatePreset: {
            const entityTitle = finaliseTitle(componentsById[action.targetIds[0]]?.title);
            return `${finaliseTitle(action.entityAnimation)}: ${entityTitle}`;
        }
        case IActionCategory.enlarge: {
            const imageTitle = finaliseTitle(componentsById[action.entityId]?.title);
            return `Enlarge: ${imageTitle}`;
        }
        case IActionCategory.linkPage: {
            return `Link to web: ${action.pageUrl || ' - '}`;
        }
        case IActionCategory.linkScene: {
            const sceneTitle = finaliseTitle(componentsById[action.sceneId]?.title);
            return `Link to scene: ${sceneTitle}`;
        }
        case IActionCategory.linkSocial: {
            const { type: _t, ..._provider } = action;
            const providerKey = Object.keys(_provider)[0];
            let provider = finaliseTitle(providerKey);
            if (providerKey === 'custom')
                provider = _provider[providerKey];
            return `Link to social: ${provider}`;
        }
        case IActionCategory.playVideo: {
            const videoTitle = finaliseTitle(componentsById[action.targetIds[0]]?.title);
            return `Play video: ${videoTitle}`;
        }
        case IActionCategory.saveContact: {
            return `Save contact ${finaliseTitle(action.name)}`;
        }
        case IActionCategory.takePhoto:
            return 'Take photo';
        case IActionCategory.animateParticle: {
            const emitterTitle = finaliseTitle(componentsById[action.targetIds[0]]?.title);
            return `Animate particle: ${emitterTitle}`;
        }
        case IActionCategory.toggleVisibility: {
            const entityTitle = finaliseTitle(componentsById[action.targetIds[0]]?.title);
            return `Toggle visibility: ${entityTitle}`;
        }
        case IActionCategory.scorm: {
            return `Course event: ${capitalize(action.event)}`;
        }
        case IActionCategory.multiImageScan: {
            return `Look for target images`;
        }
        case IActionCategory.noAction:
            return 'No action set';
    }
    return '';
};
export const isEmptyTextFieldInActionData = (actionData) => {
    if (!actionData)
        return false;
    if (actionData.type === IActionCategory.linkPage) {
        if (!actionData.pageUrl.trim())
            return true;
    }
    if (actionData.type === IActionCategory.playSound) {
        if (!actionData.filestoreId)
            return true;
    }
    if (actionData.type === IActionCategory.linkScene) {
        if (!actionData.sceneId)
            return true;
    }
    if (actionData.type === IActionCategory.linkSocial) {
        const socialProfileUrl = Object.values(ISocialProvider).reduce((acc, socialProvider) => {
            if (!!actionData[socialProvider]?.trim() && !acc)
                acc = true;
            return acc;
        }, false);
        return !socialProfileUrl;
    }
    if (actionData.type === IActionCategory.dialNumber) {
        if (!actionData.tel.trim())
            return true;
    }
    if (actionData.type === IActionCategory.composeEmail) {
        if (!actionData.recipient.trim())
            return true;
    }
    return false;
};
export const actionIcon = (action) => {
    // Check for missing fields
    if (isEmptyTextFieldInActionData(action)) {
        return _jsx(Danger16, {});
    }
    switch (action.type) {
        case IActionCategory.animateParticle:
            return _jsx(AnimateParticle16, {});
        case IActionCategory.dialNumber:
            return _jsx(Phone16, {});
        case IActionCategory.playSound:
            return _jsx(Sound16, {});
        case IActionCategory.composeEmail:
            return _jsx(Mail16, {});
        case IActionCategory.animateModel:
        case IActionCategory.animatePreset:
            return _jsx(AnimateModel16, {});
        case IActionCategory.linkPage:
            return _jsx(Globe16, {});
        case IActionCategory.linkScene:
            return _jsx(Scene16, {});
        case IActionCategory.linkSocial:
            return _jsx(Hash16, {});
        case IActionCategory.playVideo:
            return _jsx(Scene16, {});
        case IActionCategory.enlarge:
            return _jsx(Expand16, {});
        case IActionCategory.saveContact:
            return _jsx(User16, {});
        case IActionCategory.takePhoto:
            return _jsx(Camera16, {});
        case IActionCategory.toggleVisibility:
            return _jsx(View16, {});
        case IActionCategory.multiImageScan:
            return _jsx(SearchIcon, {});
        case IActionCategory.scorm:
            return _jsx(Learn16, {});
    }
    return _jsx(_Fragment, {});
};
export const newScormEventAction = () => {
    return {
        type: IActionCategory.scorm,
        event: IScormEventType.progress,
        progress: 0,
    };
};
export const newDialNumberAction = () => {
    return {
        type: IActionCategory.dialNumber,
        tel: '',
    };
};
export const newPlaySoundAction = () => {
    return {
        type: IActionCategory.playSound,
        filestoreId: '',
        stopMedia: false,
        loop: false,
        title: '',
    };
};
export const newComposeEmailAction = () => {
    return {
        type: IActionCategory.composeEmail,
        recipient: '',
        subject: '',
        body: '',
    };
};
export const newAnimateModelAction = (targetIds, animationName) => {
    return {
        type: IActionCategory.animateModel,
        targetIds,
        animationName: animationName,
        animationRepetitions: 1,
        animationRunCounter: 1,
    };
};
export const newAnimateEmitterAction = (targetIds) => {
    const action = {
        type: IActionCategory.animateParticle,
        targetIds,
        behaviour: 'start',
        duration: null,
    };
    return action;
};
export const newToggleVisibilityAction = (targetIds) => {
    return {
        type: IActionCategory.toggleVisibility,
        targetIds,
        behaviour: 'show',
    };
};
export const newPlayVideoAction = (targetIds) => {
    return {
        type: IActionCategory.playVideo,
        targetIds,
    };
};
export const newLinkPageAction = () => {
    return {
        type: IActionCategory.linkPage,
        pageUrl: '',
    };
};
export const newLinkSocialAction = () => {
    return {
        type: IActionCategory.linkSocial,
    };
};
export const newTakePhotoAction = () => {
    return {
        type: IActionCategory.takePhoto,
    };
};
export const newEnlargeAction = () => {
    return {
        type: IActionCategory.enlarge,
        entityId: '',
    };
};
export const newLinkSceneAction = (currentSceneId) => {
    return {
        type: IActionCategory.linkScene,
        sceneId: currentSceneId,
    };
};
export const newAnimatePresetMoveAction = (targetId) => {
    return {
        type: IActionCategory.animatePreset,
        duration: 1000,
        delay: 0,
        loopCount: null,
        easing: IEasing.linear,
        backAndForth: true,
        entityAnimation: IPresetAnimation.move,
        resetOnFinish: false,
        position: [0, 0, 0],
        targetIds: [targetId]
    };
};
export const newAnimatePresetSpinAction = (targetId) => {
    return {
        type: IActionCategory.animatePreset,
        duration: 1000,
        delay: 0,
        loopCount: null,
        easing: IEasing.linear,
        backAndForth: true,
        entityAnimation: IPresetAnimation.spin,
        targetIds: [targetId],
        rotation: [0, -1, 0]
    };
};
export const newAnimatePresetScaleAction = (targetId) => {
    return {
        type: IActionCategory.animatePreset,
        duration: 1000,
        delay: 0,
        loopCount: null,
        easing: IEasing.linear,
        backAndForth: true,
        entityAnimation: IPresetAnimation.scale,
        targetIds: [targetId],
        multiplier: 1.5,
        resetOnFinish: false
    };
};
export const newAnimatePresetOrbitAction = (targetId) => {
    return {
        type: IActionCategory.animatePreset,
        duration: 3000,
        delay: 0,
        loopCount: null,
        easing: IEasing.linear,
        backAndForth: false,
        entityAnimation: IPresetAnimation.orbit,
        targetIds: [targetId],
        pivot: [0, 0, 0],
        angle: Math.PI * 2,
        axis: [0, -1, 0]
    };
};
export const newMultiImageScanAction = () => {
    return {
        type: IActionCategory.multiImageScan
    };
};
export const newSaveContactAction = () => {
    return {
        type: IActionCategory.saveContact,
        name: '',
        phone: '',
        company: '',
        email: '',
        website: '',
        phone2: '',
        phone3: '',
        street1: '',
        street2: '',
        city: '',
        county: '',
        zip: '',
        country: '',
        imgUrl: '',
    };
};
export const socialsData = [
    {
        provider: ISocialProvider.facebook,
        svg: FacebookIcon,
        regex: regex.Facebook,
    },
    {
        provider: ISocialProvider.youtube,
        svg: YoutubeIcon,
        regex: regex.Youtube,
    },
    {
        provider: ISocialProvider.instagram,
        svg: InstagramIcon,
        regex: regex.Instagram,
    },
    {
        provider: ISocialProvider.reddit,
        svg: RedditIcon,
        regex: regex.Reddit,
    },
    {
        provider: ISocialProvider.tiktok,
        svg: TiktokIcon,
        regex: regex.TikTok,
    },
    {
        provider: ISocialProvider.soundCloud,
        svg: SoundcloudIcon,
        regex: regex.SoundCloud,
    },
    {
        provider: ISocialProvider.twitch,
        svg: TwitchIcon,
        regex: regex.Twitch,
    },
    {
        provider: ISocialProvider.linkedIn,
        svg: LinkedinIcon,
        regex: regex.LinkedIn,
    },
    {
        provider: ISocialProvider.vimeo,
        svg: VimeoIcon,
        regex: regex.Vimeo,
    },
    {
        provider: ISocialProvider.spotify,
        svg: SpotifyIcon,
        regex: regex.Spotify,
    },
    {
        provider: ISocialProvider.twitter,
        svg: TwitterIcon,
        regex: regex.Twitter,
    },
    {
        provider: ISocialProvider.custom,
        svg: WebsiteIconSvg,
        regex: regex.custom,
    },
];
// Given an action which has a linkSocial property, will return the social network that is selected
export const getSelectedSocialNetwork = (action) => {
    if (Object.keys(action).length == 1 || action.type !== IActionCategory.linkSocial)
        return ISocialProvider.custom;
    const { type: _type, ...rest } = action;
    return Object.keys(rest).reduce((acc, val) => {
        if (val)
            acc = val;
        return acc;
    }, ISocialProvider.custom);
};
// Returns true / false if an entity already has one of linkPage, linkSocial, linkScene, dialNumber, saveContact, composeEmail actions
export const componentHasBlockingActionForTrigger = (component, triggerType) => {
    if (!component.actions || !component.actions[triggerType] || !component.actions[triggerType]?.length)
        return false;
    if (!component.actions)
        return false;
    for (let i = 0; i < component.actions[triggerType].length; i++) {
        if ([IActionCategory.linkPage, IActionCategory.linkScene, IActionCategory.multiImageScan, IActionCategory.linkSocial, IActionCategory.composeEmail, IActionCategory.dialNumber, IActionCategory.saveContact].includes(component.actions[triggerType][i].type)) {
            return true;
        }
    }
    return false;
};
// Returns true if entity already has one of animateModel, enlarge, playSound, playVideo
export const componentHasNonBlockingActionForTrigger = (component, triggerType) => {
    if (!component.actions || !component.actions[triggerType] || !component.actions[triggerType]?.length)
        return false;
    for (let i = 0; i < component.actions[triggerType].length; i++) {
        if ([IActionCategory.takePhoto, IActionCategory.animateModel, IActionCategory.animateParticle, IActionCategory.enlarge, IActionCategory.playSound, IActionCategory.playVideo].includes(component.actions[triggerType][i].type)) {
            return true;
        }
    }
    return false;
};
export const componentHasActionForTrigger = (component, action, triggerType) => {
    if (!component.actions || !component.actions[triggerType] || !component.actions[triggerType]?.length)
        return false;
    for (let i = 0; i < component.actions[triggerType].length; i++) {
        if (action === component.actions[triggerType][i].type)
            return true;
    }
    return false;
};
export const getAllActionTargetIdsFromEntityForTrigger = (entity, triggerType) => {
    if (!entity?.actions || !entity?.actions?.[triggerType] || !entity?.actions?.[triggerType]?.length)
        return [];
    let targetIds = [];
    for (let i = 0; i < entity.actions[triggerType].length; i++) {
        const action = entity.actions[triggerType][i];
        if (action.type === IActionCategory.playVideo ||
            action.type === IActionCategory.toggleVisibility ||
            action.type === IActionCategory.animateModel ||
            action.type === IActionCategory.animateParticle) {
            targetIds = [...targetIds, ...action.targetIds];
        }
    }
    return targetIds;
};
export const getSocialIcon = (socialNetwork) => {
    switch (socialNetwork) {
        case ISocialProvider.facebook:
            return _jsx(Facebook16, {});
        case ISocialProvider.instagram:
            return _jsx(Instagram16, {});
        case ISocialProvider.linkedIn:
            return _jsx(LinkedIn16, {});
        case ISocialProvider.reddit:
            return _jsx(Reddit16, {});
        case ISocialProvider.soundCloud:
            return _jsx(SoundCloud16, {});
        case ISocialProvider.spotify:
            return _jsx(Spotify16, {});
        case ISocialProvider.tiktok:
            return _jsx(TikTok16, {});
        case ISocialProvider.twitch:
            return _jsx(Twitch16, {});
        case ISocialProvider.twitter:
            return _jsx(Twitter16, {});
        case ISocialProvider.vimeo:
            return _jsx(Vimeo16, {});
        case ISocialProvider.youtube:
            return _jsx(YouTube16, {});
        case ISocialProvider.noSocial:
        case ISocialProvider.custom:
            return _jsx(Globe16, {});
    }
    return _jsx(_Fragment, {});
};
export const actionDetail = (action, entityTitles, sceneTitlesDict) => {
    switch (action.type) {
        case IActionCategory.scorm: {
            const eventTitle = action.event === IScormEventType.progress ? 'Progress' : 'Completion';
            return (_jsxs("div", { children: [_jsx("span", { children: `Event: ${eventTitle}` }), action.event === IScormEventType.progress && _jsxs("span", { children: ["Progress: ", Math.floor(action.progress * 100), "%"] })] }));
        }
        case IActionCategory.dialNumber:
            return (_jsx("div", { children: _jsxs("span", { children: ["Dial number: ", action.tel] }) }));
        case IActionCategory.takePhoto:
            return (_jsx("div", { children: _jsx("span", { children: "A photo will be taken on tap" }) }));
        case IActionCategory.playSound:
            return (_jsxs("div", { children: [_jsxs("span", { children: ["Sound: ", action.title] }), _jsxs("span", { children: ["Stop other media: ", action.stopMedia ? 'Yes' : 'No'] }), _jsxs("span", { children: ["Loop: ", action.loop ? 'Yes' : 'No'] })] }));
        case IActionCategory.composeEmail: {
            const textArray = action.body?.split('%0D%0A');
            return (_jsxs("div", { children: [_jsxs("span", { style: { paddingBottom: '4px' }, children: [_jsx(Mail16, {}), " ", action.recipient] }), _jsxs("span", { children: [_jsx(Star16, {}), " ", action.subject] }), _jsxs("p", { style: { paddingBottom: '4px', marginTop: '0px' }, children: [_jsxs("span", { style: { textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap', width: '188px', display: 'flex', alignItems: 'center', marginBottom: '0px' }, children: [_jsx(Message16, { style: { marginRight: '8px', marginBottom: '6px' } }), _jsx("span", { children: textArray[0] })] }), textArray.slice(0, 3).map((str, i) => {
                                if (i === 0)
                                    return null;
                                return (_jsx("span", { style: { marginLeft: '22px', textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap', width: '168px', marginTop: '0px' }, children: str }, i));
                            }), textArray.length > 3 && _jsx("span", { style: { marginLeft: '20px', marginTop: '-4px' }, children: "..." })] })] }));
        }
        case IActionCategory.animateModel: {
            const modelId = action.targetIds[0];
            return (_jsxs("div", { children: [_jsxs("span", { children: ["Model: ", entityTitles[modelId]] }), _jsxs("span", { children: ["Animation: ", action.animationName] }), _jsxs("span", { children: ["Loop: ", action.animationRepetitions ? action.animationRepetitions : 'Forever'] }), action.isClampWhenFinished && _jsx("span", { children: "Finish: hold last frame" })] }));
        }
        case IActionCategory.animateParticle: {
            const { duration = null, behaviour, targetIds } = action;
            const emitterId = targetIds[0];
            return (_jsxs("div", { children: [_jsx("span", { children: `Particle: ${entityTitles[emitterId]}` }), _jsx("span", { children: `Action: ${behaviour.charAt(0).toUpperCase() + behaviour.slice(1)}` }), behaviour !== 'stop' && _jsx("span", { children: `Particle duration: ${duration === null ? 'Forever' : `${duration} s`}` })] }));
        }
        case IActionCategory.toggleVisibility: {
            const { behaviour, targetIds } = action;
            const entityId = targetIds[0];
            return (_jsxs("div", { children: [_jsx("span", { children: `Object: ${entityTitles[entityId]}` }), _jsx("span", { children: `Action: ${behaviour.charAt(0).toUpperCase() + behaviour.slice(1)}` })] }));
        }
        case IActionCategory.linkPage: {
            const url = action.pageUrl;
            return (_jsx("div", { children: _jsx("span", { children: _jsx("a", { target: "_blank", href: addUrlScheme(url), rel: "noreferrer", children: url }) }) }));
        }
        case IActionCategory.linkScene: {
            const sceneId = action.sceneId;
            return (_jsx("div", { children: _jsxs("span", { children: ["Scene: ", sceneTitlesDict[sceneId]] }) }));
        }
        case IActionCategory.playVideo:
            return (_jsx("div", { children: _jsxs("span", { children: ["Video: ", entityTitles[action.targetIds[0]]] }) }));
        case IActionCategory.enlarge:
            return (_jsx("div", { children: _jsx("span", { children: "The image will enlarge on tap" }) }));
        case IActionCategory.linkSocial: {
            const selectedSocialNetwork = getSelectedSocialNetwork(action);
            if (selectedSocialNetwork === ISocialProvider.custom)
                break;
            if (!selectedSocialNetwork)
                break;
            const socialActionValue = action[selectedSocialNetwork];
            if (!socialActionValue)
                break;
            const username = getUsernameFromSocialUrl(socialActionValue);
            const socialIcon = getSocialIcon(selectedSocialNetwork);
            return (_jsx("div", { children: _jsxs("span", { style: { paddingBottom: '4px' }, children: [socialIcon, ' ', _jsx("a", { href: action[selectedSocialNetwork], target: "_blank", rel: "noreferrer", children: username || action[selectedSocialNetwork] })] }) }));
        }
        case IActionCategory.saveContact: {
            const { name: _name, phone: _phone, email: _email, type: _type, ...rest } = action;
            const fields = Object.keys(rest);
            let hasMoreValues = false;
            for (let i = 0; i < fields.length; i++) {
                const field = fields[i];
                if (action[field])
                    hasMoreValues = true;
            }
            return (_jsxs("div", { children: [_jsxs("span", { style: { paddingBottom: '4px' }, children: [_jsx(User16, {}), " ", action?.name] }), _jsxs("span", { style: { paddingBottom: '4px' }, children: [_jsx(Phone16, {}), " ", action?.phone] }), !!action?.email && (_jsxs("span", { style: { paddingBottom: '4px' }, children: [_jsx(Mail16, {}), " ", action?.email] })), hasMoreValues && (_jsxs("span", { style: { paddingBottom: '4px' }, children: [_jsx(Plus16, {}), "additional fields"] }))] }));
        }
    }
    return _jsx(_Fragment, {});
};
const parseSocialUrl = (url) => {
    let isValid = false;
    for (let i = 0; i < socialsData.length; i++) {
        const { regex, provider: _social } = socialsData[i];
        if (regex && !!url.match(regex)) {
            isValid = true;
        }
    }
    return { isValid };
};
export const validateAction = (action) => {
    if (!action)
        return null;
    switch (action.type) {
        case IActionCategory.composeEmail: {
            const { isValid } = parseEmailAddress(action.recipient);
            if (isValid)
                return null;
            return { recipient: ACTION_ERROR_MESSAGES.invalidEmail };
        }
        case IActionCategory.dialNumber: {
            const { isValid } = parsePhoneNumber(action.tel);
            if (!action.tel)
                return { tel: ACTION_ERROR_MESSAGES.missingPhoneNumber };
            if (isValid)
                return null;
            return { tel: ACTION_ERROR_MESSAGES.invalidPhoneNumber };
        }
        case IActionCategory.linkPage: {
            const isValid = true; //parseCustomUrl(action.pageUrl);
            if (isValid)
                return null;
            return { pageUrl: ACTION_ERROR_MESSAGES.invalidWebsite };
        }
        case IActionCategory.saveContact: {
            const errors = {};
            // obligatory inputs
            if (!action.name) {
                errors.name = ACTION_ERROR_MESSAGES.missingName;
            }
            if (!action.phone) {
                errors.phone = ACTION_ERROR_MESSAGES.missingPhoneNumber;
            }
            else if (!parsePhoneNumber(action.phone).isValid) {
                errors.phone = ACTION_ERROR_MESSAGES.invalidPhoneNumber;
            }
            // optional inputs
            if (action.phone2 && !parsePhoneNumber(action.phone2).isValid) {
                errors.phone2 = ACTION_ERROR_MESSAGES.invalidPhoneNumber;
            }
            if (action.phone3 && !parsePhoneNumber(action.phone3).isValid) {
                errors.phone3 = ACTION_ERROR_MESSAGES.invalidPhoneNumber;
            }
            if (action.email && !parseEmailAddress(action.email).isValid) {
                errors.email = ACTION_ERROR_MESSAGES.invalidEmail;
            }
            if (action.website && !parseCustomUrl(action.website).isValid) {
                errors.website = ACTION_ERROR_MESSAGES.invalidWebsite;
            }
            return Object.keys(errors).length > 0 ? errors : null;
        }
        case IActionCategory.linkSocial: {
            const socialNetwork = getSelectedSocialNetwork(action);
            if (socialNetwork) {
                const url = action[socialNetwork];
                // No URL entered
                if (!url?.length) {
                    const error = {
                        invalidProvider: ACTION_ERROR_MESSAGES.missingSocial,
                    };
                    return error;
                }
                // Not a valid URL for the given social network
                if (!parseSocialUrl(url).isValid) {
                    if (socialNetwork !== ISocialProvider.custom) {
                        const error = {
                            invalidProvider: ACTION_ERROR_MESSAGES.invalidSocial,
                        };
                        return error;
                    }
                    else {
                        // "Custom" so it's actually an invalid URL, not invalid social URL
                        const error = {
                            invalidProvider: ACTION_ERROR_MESSAGES.invalidWebsite,
                        };
                        return error;
                    }
                }
                return null;
            }
        }
    }
    return null;
};
export const toggleVisibilityEntitySort = (componentsById) => {
    return (a, b) => {
        const componentA = componentsById[a];
        const componentB = componentsById[b];
        const aTypeWeight = getComponentTypeSortWeight(componentA.type);
        const bTypeWeight = getComponentTypeSortWeight(componentB.type);
        if (aTypeWeight === bTypeWeight && aTypeWeight > 0) {
            if (isAbstractComponent(componentA) || isAbstractComponent(componentB))
                return 0;
            return componentA.title.localeCompare(componentB.title);
        }
        else {
            return aTypeWeight - bTypeWeight;
        }
    };
};
const getComponentTypeSortWeight = (componentType) => {
    switch (componentType) {
        case IComponentType.Button:
            return 1;
        case IComponentType.Text:
            return 2;
        case IComponentType.Image:
            return 3;
        case IComponentType.Video:
            return 4;
        case IComponentType.Model3d:
            return 5;
        case IComponentType.Emitter:
            return 6;
    }
    return 0;
};
